import React, { useCallback, useRef, useState } from "react";
import { Icon } from "@fluentui/react";
import styled from "styled-components";
import { FilterPopover, FilterPopoverConfig } from "./FilterPopover";
import { SerializedFilter } from "../../store/tableFilters";

type FilterableHeaderCellProps = {
  columnKey: string;
  columnLabel: string;
  isSortable?: boolean;
  sortIcon?: string;
  filterConfig?: FilterPopoverConfig;
  currentFilter?: SerializedFilter;
  isFiltered?: boolean;
  onSort?: () => void;
  onApplyFilter?: (filter: SerializedFilter) => void;
  onClearFilter?: () => void;
  children?: React.ReactNode;
};

export const FilterableHeaderCell = ({
  columnKey,
  columnLabel,
  isSortable = false,
  sortIcon = "Sort",
  filterConfig,
  currentFilter,
  isFiltered = false,
  onSort,
  onApplyFilter,
  onClearFilter,
  children,
}: FilterableHeaderCellProps) => {
  const [isHovered, setIsHovered] = useState(false);
  const headerRef = useRef<HTMLDivElement>(null);

  const handleMouseEnter = useCallback(() => {
    setIsHovered(true);
  }, []);

  const handleMouseLeave = useCallback(() => {
    setIsHovered(false);
  }, []);

  const handleSortClick = useCallback(() => {
    if (isSortable && onSort) {
      onSort();
    }
  }, [isSortable, onSort]);

  const showFilterIcon = filterConfig && (isHovered || isFiltered);

  return (
    <HeaderCellContainer
      ref={headerRef}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      <HeaderContent onClick={handleSortClick} $isSortable={isSortable}>
        {children || <HeaderLabel>{columnLabel}</HeaderLabel>}
        {isSortable && (
          <SortIconContainer>
            <Icon iconName={sortIcon} />
          </SortIconContainer>
        )}
      </HeaderContent>

      {showFilterIcon && filterConfig && onApplyFilter && onClearFilter && (
        <FilterIconContainer>
          <FilterPopover
            columnKey={columnKey}
            columnLabel={columnLabel}
            filterConfig={filterConfig}
            currentFilter={currentFilter}
            isFiltered={isFiltered}
            onApplyFilter={onApplyFilter}
            onClearFilter={onClearFilter}
          />
        </FilterIconContainer>
      )}
    </HeaderCellContainer>
  );
};

const HeaderCellContainer = styled.div`
  display: flex;
  align-items: center;
  width: 100%;
  height: 100%;
  position: relative;
  gap: 4px;
`;

const HeaderContent = styled.div<{ $isSortable: boolean }>`
  display: flex;
  align-items: center;
  flex: 1;
  min-width: 0;
  cursor: ${(props) => (props.$isSortable ? "pointer" : "default")};

  &:hover {
    opacity: ${(props) => (props.$isSortable ? 0.8 : 1)};
  }
`;

const HeaderLabel = styled.span`
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const SortIconContainer = styled.span`
  margin-left: 4px;
  display: flex;
  align-items: center;
  font-size: 12px;
`;

const FilterIconContainer = styled.div`
  display: flex;
  align-items: center;
  flex-shrink: 0;
`;

export default FilterableHeaderCell;
